/**
 * What the Wizard answers when a sentence matches no scripted scenario.
 *
 * Home's composer now sends every un-commanded sentence here — `create`, `edit`
 * and `delete` are the only three words that still open a screen — so this is
 * the answer most people meet first. It used to be one line ("Got it. Try
 * 'migrate Relay to native'"), which is a dead end for every other question a
 * person might reasonably type on the front page.
 *
 * So it reads the org's own dataset and answers about it: how many agents are
 * running, how the connectors are holding up, what is waiting in the Inbox —
 * and hands over a link to the screen that holds the detail. Every number is
 * counted from the same dataset the screens render, so the Wizard can never
 * disagree with the page behind it; nothing here is invented demo fiction.
 *
 * Lives in `gateway/` rather than `demo/` because both gateways answer with it:
 * the demo one when no scenario matches, and the API one because the BFF's
 * Wizard is still a stub with a single canned line. It is a reading of a
 * dataset, not demo fiction, so neither side owns it.
 */
import { healthBreakdown } from '@wand/shared/dataset/connectors.js';
import type { OrgDataset } from './dataset.js';
import type { WizardEvent } from './wizard.js';

/** The closing line every answer ends on: what the composer still routes. */
const COMMANDS =
  'Anything else you type here I’ll answer like this. Say “create”, “edit” or ' +
  '“delete” and I’ll take you straight to the maker instead.';

type Topic = 'capabilities' | 'integrations' | 'agents' | 'processes' | 'inbox' | 'workspace';

/**
 * Which shelf of the org the sentence is reaching for.
 *
 * Order is the priority: a sentence about a failing connector mentions both
 * "connector" and "fix", and the connector is the subject. `capabilities` is
 * first but deliberately anchored to the start of the sentence, so "help me
 * with the Workday connector" is still an integrations question.
 */
function topicOf(text: string): Topic {
  const t = ` ${text.toLowerCase()} `;
  if (/^\s*(what can you do|what do you do|who are you|what are you|help)\b/i.test(text))
    return 'capabilities';
  if (/\b(connector|connectors|integration|integrations|hub|oauth|token|webhook|sync|api)\b/.test(t))
    return 'integrations';
  if (/\b(agent|agents|workforce|autonomy|autonomous|copilot)\b/.test(t)) return 'agents';
  if (/\b(process|processes|workflow|workflows|flow|flows|stage|stages|pipeline)\b/.test(t))
    return 'processes';
  if (/\b(inbox|approval|approvals|approve|waiting|pending|queue|assigned)\b/.test(t)) return 'inbox';
  return 'workspace';
}

/** `3 things` / `1 thing` — so no answer reads "1 connectors". */
function count(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** `a, b and c` from a list, capped so an answer never turns into a roster. */
function list(names: string[], cap = 3): string {
  const shown = names.slice(0, cap);
  const rest = names.length - shown.length;
  const joined =
    shown.length <= 1
      ? (shown[0] ?? '')
      : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  return rest > 0 ? `${joined} (+${rest} more)` : joined;
}

/**
 * The answer, as the events the dock renders: a reasoning thread, the answer
 * itself with its sources, the one thing worth pulling out as an insight, and a
 * link to the screen that owns the detail.
 */
export function fallbackAnswer(text: string, ds: OrgDataset): WizardEvent[] {
  switch (topicOf(text)) {
    case 'capabilities':
      return capabilities(ds);
    case 'integrations':
      return integrations(ds);
    case 'agents':
      return agents(ds);
    case 'processes':
      return processes(ds);
    case 'inbox':
      return inbox(ds);
    default:
      return workspace(ds);
  }
}

function capabilities(ds: OrgDataset): WizardEvent[] {
  const listed = ds.agents.filter((a) => a.listed !== false);
  return [
    {
      type: 'message',
      text:
        `I can read everything this workspace has and answer about it — the ${count(
          listed.length,
          'agent',
        )}, the ${count(ds.processes.length, 'process', 'processes')}, the ${count(
          (ds.connectors ?? []).length,
          'connector',
        )} and what is waiting in the Inbox. Ask me “how are my connectors doing”, ` +
        `“what is waiting on me”, or name a thing and I’ll pull up its record.\n\n${COMMANDS}`,
      sources: ['Workspace graph'],
    },
    { type: 'link', label: 'Integration Hub', href: '/integrations/connectors' },
    { type: 'link', label: 'Agents', href: '/agents' },
    { type: 'link', label: 'Inbox', href: '/inbox' },
    { type: 'done' },
  ];
}

function integrations(ds: OrgDataset): WizardEvent[] {
  const all = ds.connectors ?? [];
  // A draft that was never published has no health to report: it is said apart, by name, rather
  // than counted as healthy or left out of a total that then does not add up.
  const notLive = all.filter((c) => !c.live && c.status === 'draft');
  const cs = all.filter((c) => !notLive.includes(c));
  const h = healthBreakdown(cs);
  const hurting = cs.filter((c) => c.health === 'critical' || c.health === 'warning');
  const worst = cs.find((c) => c.health === 'critical') ?? hurting[0];
  const out: WizardEvent[] = [
    {
      type: 'thinking',
      steps: [
        { thought: 'That is a question about the connected apps — read the hub registry.' },
        {
          tool: {
            name: 'hub.connectors',
            input: 'health, all connectors',
            output: `${cs.length} registered · ${h.healthy} healthy, ${h.warning} degraded, ${h.critical} failing`,
          },
        },
      ],
    },
    {
      type: 'message',
      text: all.length
        ? `${count(all.length, 'connector')} registered: ${h.healthy} healthy, ${
            h.warning
          } degraded, ${h.critical} failing${
            notLive.length
              ? `, and ${notLive.length} not live — ${list(notLive.map((c) => c.name))}`
              : ''
          }.${
            hurting.length
              ? ` The ones not clean right now are ${list(hurting.map((c) => c.name))}.`
              : ' None of the live ones is degraded or failing.'
          }\n\n${COMMANDS}`
        : `This org has no connectors yet — nothing is connected for an agent to call.\n\n${COMMANDS}`,
      sources: ['Integration Hub registry', 'Connector health checks'],
    },
  ];
  if (worst) {
    out.push({
      type: 'insight',
      title: `${worst.name} is ${worst.health === 'critical' ? 'failing' : 'degraded'}`,
      body: `Its page has the health timeline and the last executions — that is where you can see what broke before you change anything.`,
      sources: ['Connector health checks'],
    });
    out.push({
      type: 'link',
      label: `${worst.name} · Health`,
      href: `/integrations/connectors/${worst.id}?tab=health`,
    });
  }
  out.push({ type: 'link', label: 'Integration Hub', href: '/integrations/connectors' });
  out.push({ type: 'done' });
  return out;
}

function agents(ds: OrgDataset): WizardEvent[] {
  const listed = ds.agents.filter((a) => a.listed !== false);
  const active = listed.filter((a) => a.status === 'Active');
  // "Running" is what an autonomous agent does between requests; an interactive one is available,
  // and waits to be asked — calling it "on shift" says it is doing something it is not.
  const waiting = active.filter((a) => !a.autonomous);
  const paused = listed.filter((a) => a.status === 'Paused');
  const drafts = listed.filter((a) => a.status === 'Draft');
  const autonomous = active.filter((a) => a.autonomous);
  return [
    {
      type: 'thinking',
      steps: [
        { thought: 'A question about the workforce — count it from the agent register.' },
        {
          tool: {
            name: 'workforce.agents',
            input: 'status, autonomy',
            output: `${active.length} active · ${paused.length} paused · ${drafts.length} draft`,
          },
        },
      ],
    },
    {
      type: 'message',
      text: listed.length
        ? `${count(active.length, 'agent')} active${
            autonomous.length ? `, ${autonomous.length} of them running on their own` : ''
          }${paused.length ? `, ${paused.length} paused` : ''}${
            drafts.length ? `, ${drafts.length} still a draft` : ''
          }.${autonomous.length ? ` Running now: ${list(autonomous.map((a) => a.name))}.` : ''}${
            waiting.length ? ` Available when asked: ${list(waiting.map((a) => a.name))}.` : ''
          }\n\n${COMMANDS}`
        : `No agents in this workspace yet.\n\n${COMMANDS}`,
      sources: ['Workforce register'],
    },
    { type: 'link', label: 'Agents', href: '/agents' },
    { type: 'done' },
  ];
}

function processes(ds: OrgDataset): WizardEvent[] {
  const ps = ds.processes;
  const deployed = ps.filter((p) => p.status === 'Deployed');
  const review = ps.filter((p) => p.status === 'Needs review');
  const running = deployed.reduce((n, p) => n + (p.active ?? 0), 0);
  const recent = [...ps].sort((a, b) => a.mins - b.mins).slice(0, 3);
  const out: WizardEvent[] = [
    {
      type: 'thinking',
      steps: [
        { thought: 'A question about the processes — read the library, not the canvas.' },
        {
          tool: {
            name: 'processes.library',
            input: 'status, active runs',
            output: `${deployed.length} deployed · ${running} runs in flight`,
          },
        },
      ],
    },
    {
      type: 'message',
      text: ps.length
        ? `${count(deployed.length, 'process', 'processes')} deployed with ${count(
            running,
            'run',
          )} in flight${
            review.length ? `, and ${count(review.length, 'process', 'processes')} needing review` : ''
          }. Touched most recently: ${list(recent.map((p) => p.name))}.\n\n${COMMANDS}`
        : `No processes yet — say “create a process” and I’ll draw the first one with you.\n\n${COMMANDS}`,
      sources: ['Process library'],
    },
  ];
  if (review.length) {
    out.push({
      type: 'insight',
      title: `${count(review.length, 'process', 'processes')} waiting on a review`,
      body: `${list(review.map((p) => p.name))} — deployed versions keep running while a review is open, so this is not blocking anything today.`,
      sources: ['Process library'],
    });
  }
  out.push({ type: 'link', label: 'Processes', href: '/processes' });
  out.push({ type: 'done' });
  return out;
}

function inbox(ds: OrgDataset): WizardEvent[] {
  const pending = ds.inbox.filter((i) => i.status === 'pending');
  const byCat = new Map<string, number>();
  for (const i of pending) byCat.set(i.cat, (byCat.get(i.cat) ?? 0) + 1);
  const breakdown = [...byCat].map(([cat, n]) => `${n} ${cat}`).join(', ');
  return [
    {
      type: 'thinking',
      steps: [
        { thought: 'That is about what is waiting — read the Inbox, pending only.' },
        {
          tool: {
            name: 'inbox.pending',
            input: 'status = pending',
            output: breakdown || 'nothing pending',
          },
        },
      ],
    },
    {
      type: 'message',
      text: pending.length
        ? `${count(pending.length, 'item')} still open in your Inbox${
            breakdown ? ` — ${breakdown}` : ''
          }. The oldest is “${pending[pending.length - 1]?.title}”.\n\n${COMMANDS}`
        : `Your Inbox is clear — nothing is waiting on you.\n\n${COMMANDS}`,
      sources: ['Inbox'],
    },
    { type: 'link', label: 'Inbox', href: '/inbox' },
    { type: 'done' },
  ];
}

function workspace(ds: OrgDataset): WizardEvent[] {
  const listed = ds.agents.filter((a) => a.listed !== false);
  const active = listed.filter((a) => a.status === 'Active');
  const all = ds.connectors ?? [];
  // As in the connectors answer: a draft never published is registered, not wired up, and its
  // unknown health is not "healthy". It is said apart, so the summary never counts it as working.
  const notLive = all.filter((c) => !c.live && c.status === 'draft');
  const cs = all.filter((c) => !notLive.includes(c));
  // Each by its short name ("Slack"), unless two share one — then by the name the hub shows.
  const short = notLive.map((c) => c.name.split(' · ')[0] ?? c.name);
  const notLiveNames = new Set(short).size === short.length ? short : notLive.map((c) => c.name);
  const h = healthBreakdown(cs);
  const pending = ds.inbox.filter((i) => i.status === 'pending');
  const hurting = h.warning + h.critical;
  return [
    {
      type: 'thinking',
      steps: [
        { thought: 'No single shelf named — answer from the whole workspace and offer the doors.' },
        {
          tool: {
            name: 'workspace.rollup',
            input: 'agents, processes, connectors, inbox',
            output: `${active.length} agents · ${ds.processes.length} processes · ${cs.length} connectors${
              notLive.length ? ` live, ${notLive.length} not live` : ''
            } · ${pending.length} pending`,
          },
        },
      ],
    },
    {
      type: 'message',
      text: `Here is where the workspace stands: ${count(active.length, 'agent')} running across ${count(
        ds.processes.length,
        'process',
        'processes',
      )}, ${count(cs.length, 'connector')} wired up${
        hurting ? ` (${hurting} not fully healthy)` : ' and all healthy'
      }${
        notLive.length ? ` (${notLive.length} more registered and not live: ${list(notLiveNames)})` : ''
      }, and ${
        pending.length ? `${count(pending.length, 'item')} waiting in your Inbox` : 'a clear Inbox'
      }.\n\nAsk me about any one of those and I’ll go deeper.\n\n${COMMANDS}`,
      sources: ['Workspace graph'],
    },
    ...(hurting
      ? ([{ type: 'link', label: 'Integration Hub', href: '/integrations/connectors' }] as WizardEvent[])
      : []),
    ...(pending.length ? ([{ type: 'link', label: 'Inbox', href: '/inbox' }] as WizardEvent[]) : []),
    { type: 'link', label: 'Agents', href: '/agents' },
    { type: 'done' },
  ];
}
